import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import PageShell from "../components/sections/PageShell";
import { Section, Card, CheckList, Steps, FaqList, PageHero } from "../components/landing/Blocks";
import { frontOffice as c } from "../data/landingPages";
import { applySeo } from "../lib/seo";

const CompareTable = () => (
  <div className="overflow-x-auto rounded-md border" style={{ borderColor: "var(--ggm-border-subtle)" }}>
    <table className="w-full text-left font-body text-sm md:text-base" data-testid="fo-compare-table">
      <thead style={{ background: "var(--ggm-surface)" }}>
        <tr className="font-condensed uppercase tracking-widest text-xs text-white/60">
          <th className="p-4" /><th className="p-4 text-[var(--ggm-signal-cyan)]">AI Front Desk</th><th className="p-4">Typical monthly chatbot service</th>
        </tr>
      </thead>
      <tbody>
        {c.compare.map((r) => (
          <tr key={r[0]} className="border-t" style={{ borderColor: "var(--ggm-border-subtle)" }}>
            <td className="p-4 text-white/60 font-condensed uppercase tracking-widest text-xs">{r[0]}</td>
            <td className="p-4 text-white font-bold">{r[1]}</td>
            <td className="p-4 text-white/65">{r[2]}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const AuditCta = ({ testId }) => (
  <Link to="/audit" className="ggm-btn-primary" data-testid={testId}>
    Get my free audit <ArrowUpRight size={16} />
  </Link>
);

export default function FrontOfficePage() {
  useEffect(() => applySeo(c), []);

  return (
    <PageShell testId="front-office-page">
      <PageHero eyebrow="AI Front Desk · $1,500 fixed" h1={c.h1} lede={c.lede} badges={c.badges} testId="fo-hero">
        <Link to="/audit" className="ggm-btn-primary" data-testid="fo-hero-cta">Start with a free audit <ArrowUpRight size={16} /></Link>
        <span className="font-body text-white/60 text-sm italic">Not sure you need a chatbot? The free audit will tell you.</span>
      </PageHero>

      <Section eyebrow="The short version" title="What is the AI Front Desk?" testId="fo-what-is">
        <p className="font-body text-white/80 text-base md:text-lg leading-relaxed max-w-3xl">{c.whatIs}</p>
        <p className="mt-4 font-body text-white/65 max-w-3xl">{c.whatIsNote}</p>
      </Section>

      <Section eyebrow="Day to day" title="What it does" testId="fo-does">
        <div className="grid gap-6 md:grid-cols-2">
          {c.does.map((x, i) => (
            <Card key={x.t} testId={`fo-does-${i + 1}`}>
              <h3 className="font-condensed uppercase text-white text-xl">{x.t}</h3>
              <p className="mt-3 font-body text-white/75 leading-relaxed">{x.d}</p>
            </Card>
          ))}
        </div>
      </Section>

      <Section eyebrow="Who it's for" title="Built for businesses that lose people after hours" testId="fo-who">
        <p className="font-body text-white/80 max-w-3xl mb-6">{c.whoIntro}</p>
        <CheckList items={c.whoFit} />
      </Section>

      <Section eyebrow="A real example" title="What a front desk would fix" testId="fo-example">
        <p className="font-body text-white/80 text-base md:text-lg leading-relaxed max-w-3xl">{c.exampleIntro}</p>
        <Card className="mt-6"><h3 className="font-condensed uppercase text-white text-lg mb-4">Here's what an AI Front Desk would do there</h3><CheckList items={c.exampleDoes} /></Card>
        <p className="mt-4 font-body text-white/60 italic">{c.exampleNote}</p>
        <Link to="/projects/family-health-care-of-gallatin" className="ggm-btn-secondary mt-6" data-testid="fo-example-link">See the project <ArrowRight size={16} /></Link>
      </Section>

      <Section eyebrow="Fixed scope" title="What's included for $1,500" testId="fo-included">
        <CheckList items={c.included} />
        <p className="mt-6 font-body text-white/65"><b className="text-white">Costs extra or is a separate project:</b> {c.extras}</p>
      </Section>

      <Section eyebrow="After launch" title="What it costs after launch" testId="fo-after"><CheckList items={c.after} /></Section>

      <Section eyebrow="Own it vs rent it" title="A one-time build, not a subscription" testId="fo-compare">
        <p className="font-body text-white/75 mb-6 max-w-3xl">Many chatbot and AI receptionist services charge every month for as long as you use them. The AI Front Desk is a one-time build:</p>
        <CompareTable />
        <p className="mt-6 font-body text-white/65 max-w-3xl">{c.compareNote}</p>
      </Section>

      <Section eyebrow="Four steps" title="How it works" testId="fo-steps">
        <Steps
          steps={c.steps}
          testId="fo-step"
          renderExtra={(s) => s[2] && <Link to={s[2]} className="mt-3 inline-block underline text-[var(--ggm-signal-cyan)]" data-testid="fo-step-audit-link">Request the free audit</Link>}
        />
        <p className="mt-6 font-body text-white/70 max-w-3xl">{c.blueprint}</p>
      </Section>

      <Section eyebrow="Local builder" title="Built in Gallatin, TN" testId="fo-local">
        <p className="font-body text-white/80 text-base md:text-lg leading-relaxed max-w-3xl">{c.local}</p>
      </Section>

      <Section eyebrow="Questions, answered" title="FAQ" testId="fo-faq"><FaqList faqs={c.faqs} testId="fo-faq-list" /></Section>

      <Section eyebrow="Next step" title="Ready for a front desk that never closes?" testId="fo-final-cta">
        <p className="font-body text-white/80 max-w-3xl mb-8">
          <b className="text-white">Start with the free Local AI Audit.</b> I'll look at your website, your Google listing and how you show up in AI answers, and tell you honestly whether the AI Front Desk is the right first step.
        </p>
        <div className="flex flex-wrap gap-4 items-center">
          <AuditCta testId="fo-final-audit-cta" />
          <Link to="/#start-a-project" className="ggm-btn-secondary" data-testid="fo-start-front-desk-cta">Start my AI Front Desk <ArrowRight size={16} /></Link>
        </div>
        <p className="mt-5 font-body text-white/55 text-sm italic">No sales team. You'll hear from Robert directly.</p>
      </Section>
    </PageShell>
  );
}
